import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { US_STATES, getCitiesForState, getZipsForCity } from '../data/usLocations';

interface OfficeLocationFormProps {
  initialValues?: any;
  onSubmit: (data: any) => void;
  loading?: boolean;
}

interface FormData {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  contact_person: string;
}

const OfficeLocationForm: React.FC<OfficeLocationFormProps> = ({
  initialValues = {},
  onSubmit,
  loading,
}) => {
  const [form, setForm] = useState<FormData>({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    contact_person: '',
    ...initialValues,
  });

  useEffect(() => {
    if (initialValues) {
      setForm((prev: FormData) => ({
        ...prev,
        ...initialValues,
      }));
    }
  }, [initialValues]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name === 'state') {
      setForm(prev => ({ ...prev, state: value, city: '', zip: '' }));
    } else if (name === 'city') {
      setForm(prev => ({ ...prev, city: value, zip: '' }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const availableCities = getCitiesForState(form.state);
  const availableZips = getZipsForCity(form.state, form.city);

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 400 }}
    >
      <TextField
        label='Office Name'
        name='name'
        value={form.name}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label='Address'
        name='address'
        value={form.address}
        onChange={handleChange}
        required
        fullWidth
      />

      <FormControl fullWidth required>
        <InputLabel id='state-label'>State</InputLabel>
        <Select
          labelId='state-label'
          name='state'
          value={form.state}
          label='State'
          onChange={handleSelectChange}
        >
          <MenuItem value=''>
            <em>Select State</em>
          </MenuItem>
          {US_STATES.map(s => (
            <MenuItem key={s.abbreviation} value={s.abbreviation}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth required disabled={!form.state}>
        <InputLabel id='city-label'>City</InputLabel>
        <Select
          labelId='city-label'
          name='city'
          value={form.city}
          label='City'
          onChange={handleSelectChange}
        >
          <MenuItem value=''>
            <em>Select City</em>
          </MenuItem>
          {availableCities.map(c => (
            <MenuItem key={c.name} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth required disabled={!form.city}>
        <InputLabel id='zip-label'>ZIP Code</InputLabel>
        <Select
          labelId='zip-label'
          name='zip'
          value={form.zip}
          label='ZIP Code'
          onChange={handleSelectChange}
        >
          <MenuItem value=''>
            <em>Select ZIP Code</em>
          </MenuItem>
          {availableZips.map(z => (
            <MenuItem key={z} value={z}>
              {z}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label='Country'
        name='country'
        value={form.country}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label='Contact Person'
        name='contact_person'
        value={form.contact_person}
        onChange={handleChange}
        fullWidth
      />
      <Button type='submit' variant='contained' disabled={loading} fullWidth>
        {loading ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );
};

export default OfficeLocationForm;
